import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  HelpBanner,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { Ban, Check, Pencil, Plus, RotateCcw, X } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { messageOf } from '../lib/apiError'
import {
  CatalogToolbar,
  ErrorBanner,
  PageHeading,
  Pagination,
  StatusBadge,
} from '../components/catalog'

export interface Brand {
  brandId: number
  brand: string
  isDisabledByPlatform: boolean
  creationDate: string
  lastModificationDate: string | null
}

interface BrandPage {
  brands: Brand[]
  total: number
  offset: number
  limit: number
}

const PAGE_SIZE = 50

/**
 * BrandsPage is the platform brand catalogue — [Catalog].[Platform.Brand] in
 * the BsConnekto database.
 *
 * Retiring a brand is a soft disable (the API's DELETE sets
 * IsDisabledByPlatform): catalogue rows are referenced by other platform tables
 * and are never removed. Write controls are hidden without the catalog:write
 * capability; the backend enforces it regardless.
 */
export function BrandsPage() {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [includeDisabled, setIncludeDisabled] = useState(true)
  const [offset, setOffset] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingName, setEditingName] = useState('')
  const [error, setError] = useState('')

  const params = { search, includeDisabled: String(includeDisabled), offset, limit: PAGE_SIZE }

  const { data, isLoading } = useQuery({
    queryKey: ['brands', params],
    queryFn: async (): Promise<BrandPage> => {
      const res = await api.get('/brands', { params })
      return res.data as BrandPage
    },
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['brands'] })

  const create = useMutation({
    mutationFn: () => api.post('/brands', { brand: newName.trim(), isDisabledByPlatform: false }),
    onSuccess: () => {
      setNewName('')
      setError('')
      setDialogOpen(false)
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('brands.errors.create'))),
  })

  const rename = useMutation({
    mutationFn: (brand: Brand) =>
      api.put(`/brands/${brand.brandId}`, {
        brand: editingName.trim(),
        isDisabledByPlatform: brand.isDisabledByPlatform,
      }),
    onSuccess: () => {
      setEditingId(null)
      setEditingName('')
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('brands.errors.update'))),
  })

  const toggle = useMutation({
    mutationFn: (brand: Brand) =>
      brand.isDisabledByPlatform
        ? api.post(`/brands/${brand.brandId}/restore`)
        : api.delete(`/brands/${brand.brandId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('brands.errors.update'))),
  })

  const brands = data?.brands ?? []
  const total = data?.total ?? 0

  return (
    <div>
      <PageHeading
        title={t('brands.title')}
        subtitle={t('brands.subtitle')}
        action={
          canWrite && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger
                render={
                  <Button>
                    <Plus />
                    {t('brands.newBrand')}
                  </Button>
                }
              />
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('brands.newBrand')}</DialogTitle>
                </DialogHeader>
                <form
                  className="flex flex-col gap-4"
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (newName.trim()) create.mutate()
                  }}
                >
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="new-brand">{t('brands.columns.name')}</Label>
                    <Input
                      id="new-brand"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      maxLength={100}
                      placeholder={t('brands.newBrandPlaceholder')}
                      autoFocus
                    />
                  </div>
                  <ErrorBanner message={error} />
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button type="submit" disabled={create.isPending || !newName.trim()}>
                      {t('common.create')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )
        }
      />

      <HelpBanner storageKey="brands">
        <p>{t('brands.help.line1')}</p>
        <p>{t('brands.help.line2')}</p>
      </HelpBanner>

      <CatalogToolbar
        searchInput={searchInput}
        onSearchInput={setSearchInput}
        onSubmitSearch={() => {
          setSearch(searchInput)
          setOffset(0)
        }}
        searchPlaceholder={t('brands.searchPlaceholder')}
        includeDisabled={includeDisabled}
        onToggleDisabled={() => {
          setIncludeDisabled(!includeDisabled)
          setOffset(0)
        }}
        filtered={search !== '' || !includeDisabled}
        onClear={() => {
          setSearch('')
          setSearchInput('')
          setIncludeDisabled(true)
          setOffset(0)
        }}
      />

      {!dialogOpen && <ErrorBanner message={error} />}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-16">{t('brands.columns.id')}</TableHead>
                  <TableHead>{t('brands.columns.name')}</TableHead>
                  <TableHead className="w-32">{t('common.columns.status')}</TableHead>
                  <TableHead className="w-32">{t('common.columns.created')}</TableHead>
                  {canWrite && (
                    <TableHead className="w-28 text-right">{t('common.columns.actions')}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {brands.map((b) => (
                  <TableRow key={b.brandId}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {b.brandId}
                    </TableCell>
                    <TableCell className="font-medium">
                      {editingId === b.brandId ? (
                        <Input
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          maxLength={100}
                          autoFocus
                          className="max-w-xs"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && editingName.trim()) rename.mutate(b)
                            if (e.key === 'Escape') setEditingId(null)
                          }}
                        />
                      ) : (
                        b.brand
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge disabled={b.isDisabledByPlatform} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(b.creationDate).toLocaleDateString()}
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          {editingId === b.brandId ? (
                            <>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                title={t('common.save')}
                                disabled={rename.isPending || !editingName.trim()}
                                onClick={() => rename.mutate(b)}
                              >
                                <Check />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                title={t('common.cancel')}
                                onClick={() => setEditingId(null)}
                              >
                                <X />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                title={t('common.edit')}
                                onClick={() => {
                                  setEditingId(b.brandId)
                                  setEditingName(b.brand)
                                }}
                              >
                                <Pencil />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                title={
                                  b.isDisabledByPlatform ? t('common.restore') : t('common.retire')
                                }
                                disabled={toggle.isPending}
                                onClick={() => toggle.mutate(b)}
                              >
                                {b.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {brands.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 5 : 4}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('brands.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <Pagination
            total={total}
            offset={offset}
            pageSize={PAGE_SIZE}
            onOffset={setOffset}
            label={t('brands.total', { count: total })}
          />
        </>
      )}
    </div>
  )
}
